import { Label } from '@headlessui/react';
import { InformationCircleIcon } from '@heroicons/react/16/solid';
import { useCallback, useRef, useState } from 'react';
import { BUTTON_LABELS } from '../../constants/buttonLabels';
import { useClosePositionMutation } from '../../hooks/useClosePositionMutation';
import { useMarketOrderPriceImpact } from '../../hooks/useMarketOrderPriceImpact';
import { useModalState } from '../../hooks/useModalState';
import { usePositions } from '../../hooks/usePositions';
import { useProductPricesSub } from '../../hooks/useProductPricesSub';
import { ProductType, useProducts } from '../../hooks/useProducts';
import { usePropsAsState } from '../../hooks/usePropAsState';
import { useActiveSelectedSubaccount } from '../../hooks/useSelectedSubaccount';
import { getAmountError } from '../../lib/amountError';
import { formatDollar, formatNumberWithDecimals, formatPercentage } from '../../lib/formatters';
import { Gwei, GWEI_UNIT } from '../../lib/gwei';
import {
  alignToValidSize,
  divide,
  getDecimalPlaces,
  isMultipleOf,
  safeDivide,
} from '../../lib/math';
import { calcPnlWithFees } from '../../lib/positionHelpers';
import { estimatedClosePnlTooltip, postOnlyTooltip } from '../../lib/tooltips';
import { OrderType } from '../../types/orders';
import { Button } from '../catalyst-ui-kit/button';
import { Checkbox, CheckboxField } from '../catalyst-ui-kit/checkbox';
import { ColoredMetric } from '../ColoredMetric/ColoredMetric';
import { Input } from '../Input/Input';
import { ResponsiveModal } from '../ResponsiveModal/ResponsiveModal';
import { StyledSlider } from '../StyledSlider';
import { Tooltip } from '../Tooltip/Tooltip';

export type ClosePositionModalUiProps = {
  isOpen: boolean;
  onClose: () => void;
  onClosePosition: () => void;
  product: ProductType;
  positionSize: bigint;
  positionAvgPrice: bigint;
  executionPrice: bigint | undefined;
  midPrice: bigint | undefined;
  amount: bigint | undefined;
  setAmount: (amount: bigint | undefined) => void;
  limitPrice: bigint | undefined;
  setLimitPrice: (amount: bigint | undefined) => void;
  orderType: OrderType;
  isClosingPosition: boolean;
  amountError: string | undefined;
  oraclePrice: bigint | undefined;
  postOnly: boolean;
  setPostOnly: (value: boolean) => void;
};

export const ClosePositionModalUi: React.FC<ClosePositionModalUiProps> = ({
  isOpen,
  onClose,
  onClosePosition,
  isClosingPosition,
  product,
  positionSize,
  positionAvgPrice,
  executionPrice,
  midPrice,
  amount,
  setAmount,
  orderType,
  limitPrice,
  setLimitPrice,
  amountError,
  oraclePrice,
  postOnly,
  setPostOnly,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const isLimitOrder = orderType === 'LIMIT';
  const price = isLimitOrder ? limitPrice : executionPrice;
  const amountFormatted = amount ? Gwei.fmt(amount) : '';

  const amountSizeError =
    amount && amount > Gwei.abs(positionSize) ? 'Amount exceeds position size' : undefined;
  const error = amountError || amountSizeError;
  const hasAmountError = Boolean(error);
  const priceValidationError = (() => {
    if (limitPrice && limitPrice <= product.minPrice) {
      return `Price must be greater than ${formatNumberWithDecimals(product.minPrice)}`;
    }
    if (limitPrice && !isMultipleOf(limitPrice, product.tickSize)) {
      return `Price must be a multiple of ${formatNumberWithDecimals(product.tickSize)}`;
    }
    return '';
  })();
  const hasPriceError = Boolean(priceValidationError);

  const decimals = getDecimalPlaces(product.lotSize);
  const priceDecimals = getDecimalPlaces(product.tickSize);
  const posAbs = Gwei.abs(positionSize);

  return (
    <ResponsiveModal
      onClose={onClose}
      open={isOpen}
      title={isLimitOrder ? 'Limit Close' : 'Market Close'}
      childrenContainerClassName="p-0"
    >
      <div className="flex flex-col gap-3 sm:p-4">
        {isLimitOrder && (
          <div className="flex flex-col gap-1 text-2xs">
            <div className="flex justify-between">
              <span className="font-medium text-contentSecondary">Entry Price</span>
              <span className="text-contentPrimary ">{formatDollar(positionAvgPrice)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-contentSecondary">Oracle Price</span>
              <span className="text-contentPrimary">{formatDollar(oraclePrice)}</span>
            </div>
          </div>
        )}
        {isLimitOrder && (
          <Input
            ref={inputRef}
            label="Closing Limit Price"
            inputType="number"
            isAllowed={(values) => {
              const { floatValue = 0 } = values;
              return floatValue < Number(Gwei.fmt(product.maxPrice));
            }}
            hasError={hasPriceError}
            prefix="$"
            value={limitPrice !== undefined ? Gwei.fmt(limitPrice) : ''}
            onValueChange={(values) => {
              if (values.value === '') return setLimitPrice(undefined);

              return setLimitPrice(Gwei.toGwei(values.value));
            }}
            rightElement={
              <Button
                text
                size="xs"
                disabled={!midPrice}
                onClick={() => {
                  if (!midPrice) return;

                  setLimitPrice(alignToValidSize(midPrice, product.tickSize));
                  setTimeout(() => {
                    inputRef.current?.setSelectionRange(
                      inputRef.current.value.length,
                      inputRef.current.value.length
                    );
                  }, 50);
                }}
              >
                Mid
              </Button>
            }
            inputMode="numeric"
            decimalScale={priceDecimals}
            step={Number(Gwei.fmt(product.tickSize))}
            min={Number(Gwei.fmt(product.tickSize))}
          />
        )}
        {Boolean(hasPriceError && isLimitOrder) && (
          <p role="alert" className="flex items-center gap-1 text-xs text-contentRed">
            <InformationCircleIcon width={16} height={16} className="shrink-0" />
            {priceValidationError}
          </p>
        )}
        <Input
          label="Size"
          inputType="number"
          value={amountFormatted}
          onValueChange={(values, sourceInfo) => {
            if (sourceInfo.source !== 'event') return;
            if (values.value === '') {
              setAmount(undefined);
              return;
            }
            const valBigInt = Gwei.toGwei(values.value);
            setAmount(valBigInt);
          }}
          hasError={hasAmountError}
          rightElement={<span className="text-xs">{product?.baseTokenName}</span>}
          step={Number(Gwei.fmt(product.lotSize))}
          min={Number(Gwei.fmt(product.lotSize))}
          isAllowed={(values) => {
            const { floatValue = 0 } = values;
            return floatValue < GWEI_UNIT;
          }}
          decimalScale={decimals}
          inputMode="numeric"
        />

        {error && (
          <p role="alert" className="mt-2 flex items-center gap-1 text-xs text-contentRed">
            <InformationCircleIcon width={16} height={16} className="shrink-0" />
            {error}
          </p>
        )}
        <StyledSlider
          max={posAbs}
          value={Gwei.min(Gwei.max(amount ?? 0n, 0n), posAbs)}
          onChange={(rawAmount) => {
            const amount = alignToValidSize(rawAmount, product.lotSize);
            setAmount(amount);
          }}
          step={Gwei.max(product.lotSize, divide(posAbs, Gwei.toGwei(100)))}
          formatValue={(x) =>
            formatPercentage(safeDivide(x, Gwei.abs(positionSize)), {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })
          }
        />

        <div className="flex justify-between text-2xs text-contentSecondary">
          <div className="flex justify-center gap-1">
            Estimated close P&L{' '}
            <Tooltip content={estimatedClosePnlTooltip(orderType)}>
              <InformationCircleIcon width={12} height={12} />
            </Tooltip>
          </div>
          <span className="text-xs text-contentPrimary">
            <ColoredMetric
              formatFn={(x) => formatDollar(x, { signDisplay: 'negative' })}
              value={
                price
                  ? calcPnlWithFees(
                      price,
                      positionAvgPrice,
                      positionSize > 0n ? (amount ?? 0n) : Gwei.flipSide(amount ?? 0n),
                      isLimitOrder ? product.makerFee : product.takerFee
                    )
                  : undefined
              }
            />
          </span>
        </div>
        {isLimitOrder && (
          <CheckboxField className="w-fit">
            <Checkbox
              id="post-only-checkbox"
              color="gray"
              checked={postOnly}
              onChange={(val) => setPostOnly(val)}
            />
            <Label
              htmlFor="post-only-checkbox"
              className="cursor-pointer text-2xs font-normal text-contentSecondary"
            >
              <Tooltip content={postOnlyTooltip()}>Post Only</Tooltip>
            </Label>
          </CheckboxField>
        )}

        <div className="mt-3 grid grid-cols-2 gap-2">
          <Button color="tertiary" onClick={onClose} disabled={isClosingPosition}>
            Cancel
          </Button>
          <Button
            color="blue"
            data-testid="close-position-button"
            disabled={
              hasAmountError ||
              !amount ||
              !product ||
              (orderType === 'LIMIT' && !limitPrice) ||
              isClosingPosition
            }
            onClick={onClosePosition}
          >
            {isClosingPosition ? BUTTON_LABELS.state.closing : BUTTON_LABELS.action.confirm}
          </Button>
        </div>
      </div>
    </ResponsiveModal>
  );
};

export const ClosePositionModal: React.FC = () => {
  const { openOrClosed, productId, orderType } = useModalState((state) => state.closePositionModal);
  const setClosePositionModal = useModalState((state) => state.setClosePositionModal);
  const selectedSubaccount = useActiveSelectedSubaccount();
  const { data: positions } = usePositions({ subaccountId: selectedSubaccount?.id });
  const { data: products = [] } = useProducts();

  const product = products.find((p) => p.id === productId);
  const position = positions?.find((p) => p.productId === productId);
  const { data: pricesByProductId } = useProductPricesSub();
  const [limitPrice, setLimitPrice] = useState<bigint | undefined>();
  const [amount, setAmount] = usePropsAsState(
    position?.size ? Gwei.abs(position?.size) : undefined
  );
  const [postOnly, setPostOnly] = useState<boolean>(false);

  const closeModal = useCallback(() => {
    setLimitPrice(undefined);
    setClosePositionModal({ productId: undefined, openOrClosed: 'closed', orderType: 'LIMIT' });
  }, [setClosePositionModal]);

  const { executionPrice, midPrice, enoughLiquidity } = useMarketOrderPriceImpact(
    productId,
    position && position.size > 0 ? 'short' : 'long',
    amount || 0n
  );

  const { isClosingPosition, closePosition, dryRunResult, dryRunError } = useClosePositionMutation({
    amount,
    price: limitPrice,
    type: orderType,
    postOnly,
    position,
    product,
  });

  const handleClosePosition = useCallback(() => {
    closePosition();
    closeModal();
  }, [closePosition, closeModal]);

  if (!product || !position) {
    return null;
  }
  const { oraclePrice } = pricesByProductId[product.id] || {};

  return (
    <ClosePositionModalUi
      isOpen={openOrClosed === 'open'}
      onClose={closeModal}
      onClosePosition={handleClosePosition}
      product={product}
      positionSize={position.size}
      positionAvgPrice={position.avgPrice}
      executionPrice={executionPrice}
      midPrice={midPrice}
      postOnly={postOnly}
      setPostOnly={setPostOnly}
      amount={amount}
      setAmount={setAmount}
      orderType={orderType}
      oraclePrice={oraclePrice}
      limitPrice={limitPrice}
      setLimitPrice={setLimitPrice}
      isClosingPosition={isClosingPosition}
      amountError={
        dryRunError ||
        getAmountError({
          amount,
          dryRunResult,
          product,
          enoughLiquidity,
          orderType,
        })
      }
    />
  );
};
